import { z } from "zod";

/**
 * A Slack channel the connecting user is in, as the "add okthx to your
 * channels" picker shows it. The picker pre-selects public, internal channels
 * okthx is not in yet; the server only enforces that a join is for a channel
 * the user is in.
 */
export type SlackPickerChannel = {
  id: string;
  name: string;
  isPrivate: boolean;
  /** Shared with another organization (Slack Connect). */
  isExternal: boolean;
  /** okthx's bot is already in the channel, so there is nothing to add. */
  okthxIsMember: boolean;
};

export type SlackPickerChannelsResp = { channels: SlackPickerChannel[] };

/**
 * Whether the picker starts with a channel checked: public channels okthx is
 * not in yet. A private channel is the user's to opt into, and one shared with
 * another organization puts okthx in front of people outside it.
 */
export function isPreselectedSlackChannel(channel: SlackPickerChannel): boolean {
  return !channel.isPrivate && !channel.isExternal && !channel.okthxIsMember;
}

/**
 * Whether the picker has anything to offer: a channel the user is in that okthx
 * is not. The post-connect modal and card show only when it does.
 */
export function hasSlackChannelToAdd(channels: readonly SlackPickerChannel[]): boolean {
  return channels.some((channel) => !channel.okthxIsMember);
}

/**
 * Why the channel picker cannot run: no Slack connector for the workspace, or
 * one that has to be reconnected to grant the scopes joining needs.
 */
export type SlackPickerRefusalReason = "notConnected" | "reconnect";

export type SlackPickerRefusalResp = { reason: SlackPickerRefusalReason };

/**
 * The most channels one join request can name: as many as the picker can list
 * (`users.conversations` is read to 50 pages of 200), so a pick is never
 * refused for its size. The join itself runs in batches.
 */
export const MAX_SLACK_JOIN_CHANNELS = 10_000;

/** Slack channel ids: `C…` for public, `G…` for private channels made before 2018. */
const slackChannelIdSchema = z.string().regex(/^[CG][A-Z0-9]+$/);

export const slackTeamIdSchema = z.string().regex(/^T[A-Z0-9]+$/);

export const slackPickerChannelsQuerySchema = z.object({ teamId: slackTeamIdSchema });

export const joinSlackChannelsBodySchema = z.object({
  teamId: slackTeamIdSchema,
  channelIds: z.array(slackChannelIdSchema).min(1).max(MAX_SLACK_JOIN_CHANNELS),
});

export type JoinSlackChannelsBody = z.infer<typeof joinSlackChannelsBodySchema>;

/** How many of the requested channels okthx is being added to. */
export type JoinSlackChannelsResp = { queued: number };
